import { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CheckCircle2, RotateCcw, Target, Clock } from 'lucide-react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';

/**
 * MilestoneSlider — a live, swipeable milestone list on Master Projects.
 *
 * Right-swipe (or click "Mark done") → POST /master-projects/{id}/milestones/act
 *                                       body: { milestone_index, milestone_name, action: 'done' }
 * Left-swipe (or click "Undo")       → local-only optimistic revert. Because
 *                                       backend `milestones/act` only supports
 *                                       done/shift, a true "undo done" requires
 *                                       an edit form path — for now we soft-undo
 *                                       the visual state client-side and let
 *                                       the user re-save via the full edit dialog
 *                                       for permanent status changes.
 *
 * Behaviour (per user's choice 3b):
 *   * Right-swipe moves the milestone to `done`.
 *   * Left-swipe undoes the visual "done" if the milestone was just marked so
 *     on this session (client-side revert only; server-side rollback would
 *     require the milestone-edit form).
 */
export function MilestoneSlider({ project, canAct, onChanged }) {
  const [milestones, setMilestones] = useState(project.milestones || []);
  const [justDoneIndex, setJustDoneIndex] = useState(null); // enables the "Undo" affordance

  useEffect(() => {
    setMilestones(project.milestones || []);
  }, [project.id, project.milestones]);

  const markDone = async (i, m) => {
    try {
      const prev = m.status;
      const next = milestones.map((mm, j) => j === i ? { ...mm, status: 'done' } : mm);
      setMilestones(next);
      setJustDoneIndex(i);
      await api.post(`/master-projects/${project.id}/milestones/act`, {
        milestone_index: i,
        milestone_name: m.name,
        action: 'done',
      });
      toast.success(`"${m.name}" marked done`);
      onChanged?.();
    } catch (e) {
      // roll back on failure
      setMilestones(project.milestones || []);
      setJustDoneIndex(null);
      toast.error(parseErr(e));
    }
  };

  const undoVisual = (i) => {
    // Client-only optimistic undo of a just-marked-done milestone. If the
    // page reloads or another tab wins, the server state is still 'done'.
    // Persistent revert requires the edit dialog.
    setMilestones(prev => prev.map((mm, j) => j === i ? { ...mm, status: 'pending' } : mm));
    setJustDoneIndex(null);
    toast.message('Undone locally — re-save from the edit dialog to persist.', { duration: 3500 });
  };

  if (!milestones.length) {
    return (
      <div className="text-[12px] text-[hsl(var(--foreground-subtle))] italic px-2">
        No milestones yet.
      </div>
    );
  }

  return (
    <div className="space-y-1.5 max-w-[340px]" data-testid={`milestone-slider-${project.id}`}>
      {milestones.map((m, i) => (
        <SwipeRow
          key={`${project.id}-${i}`}
          milestone={m}
          index={i}
          canAct={canAct}
          isJustDone={justDoneIndex === i && m.status === 'done'}
          onSwipeRight={() => canAct && m.status !== 'done' && markDone(i, m)}
          onSwipeLeft={() => canAct && justDoneIndex === i && undoVisual(i)}
          onClickDone={() => canAct && m.status !== 'done' && markDone(i, m)}
          onClickUndo={() => canAct && justDoneIndex === i && undoVisual(i)}
          projectId={project.id}
        />
      ))}
    </div>
  );
}

const STATUS_TINT = {
  pending:      { bg: 'hsl(var(--surface-2))',       fg: 'hsl(var(--foreground-muted))', label: 'Pending' },
  in_progress:  { bg: 'hsl(48 96% 89%)',             fg: 'hsl(30 60% 30%)',              label: 'In progress' },
  done:         { bg: 'hsl(142 70% 90%)',            fg: 'hsl(142 50% 22%)',             label: 'Done' },
  invoiced:     { bg: 'hsl(210 90% 92%)',            fg: 'hsl(210 60% 30%)',             label: 'Invoiced' },
  paid:         { bg: 'hsl(160 70% 90%)',            fg: 'hsl(160 50% 22%)',             label: 'Paid' },
};

function SwipeRow({ milestone: m, index, canAct, isJustDone, onSwipeRight, onSwipeLeft, onClickDone, onClickUndo, projectId }) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const active = useRef(false);
  const SWIPE_THRESHOLD = 60;
  const tint = STATUS_TINT[m.status] || STATUS_TINT.pending;

  const handleStart = (clientX) => {
    if (!canAct) return;
    active.current = true;
    startX.current = clientX;
    setDragging(true);
  };
  const handleMove = (clientX) => {
    if (!active.current) return;
    const dx = clientX - startX.current;
    // clamp
    setDragX(Math.max(-140, Math.min(140, dx)));
  };
  const handleEnd = () => {
    if (!active.current) { setDragX(0); return; }
    active.current = false;
    setDragging(false);
    if (dragX > SWIPE_THRESHOLD) {
      onSwipeRight?.();
    } else if (dragX < -SWIPE_THRESHOLD) {
      onSwipeLeft?.();
    }
    setDragX(0);
  };

  return (
    <div className="relative select-none overflow-hidden rounded-lg border"
         style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--card))' }}
         data-testid={`milestone-slider-row-${projectId}-${index}`}>
      {/* Left "undo" reveal (only if just-done) */}
      {isJustDone && (
        <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-3 text-[hsl(var(--foreground-muted))]" style={{ zIndex: 0 }}>
          <RotateCcw size={13} />
          <span className="text-[11px] font-medium">Undo</span>
        </div>
      )}
      {/* Right "done" reveal */}
      {m.status !== 'done' && (
        <div className="absolute inset-y-0 left-0 flex items-center gap-1 pl-3 text-[hsl(var(--success))]" style={{ zIndex: 0 }}>
          <CheckCircle2 size={13} />
          <span className="text-[11px] font-medium">Done</span>
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        onMouseDown={(e) => handleStart(e.clientX)}
        onMouseMove={(e) => dragging && handleMove(e.clientX)}
        onMouseUp={handleEnd}
        onMouseLeave={() => { if (dragging) handleEnd(); }}
        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onTouchEnd={handleEnd}
        className="relative flex items-center gap-2 px-3 py-2 cursor-grab"
        style={{
          transform: `translateX(${dragX}px)`,
          transition: dragging ? 'none' : 'transform 220ms cubic-bezier(0.2, 0.9, 0.3, 1)',
          background: 'hsl(var(--card))',
          touchAction: 'pan-y',
          zIndex: 1,
        }}
        data-testid={`milestone-swipe-${projectId}-${index}`}
      >
        <Target size={12} className="text-[hsl(var(--foreground-subtle))] flex-none" />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-medium truncate">{m.name || '(unnamed milestone)'}</div>
          {m.target_date && (
            <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] flex items-center gap-1">
              <Clock size={10} />{m.target_date}
            </div>
          )}
        </div>
        <span
          className="text-[10px] font-semibold px-2 py-[2px] rounded-full flex-none"
          style={{ background: tint.bg, color: tint.fg }}
          data-testid={`milestone-status-${projectId}-${index}`}
        >
          {tint.label}
        </span>
        {canAct && m.status !== 'done' && (
          <button
            onClick={onClickDone}
            className="text-[10.5px] px-2 py-[3px] rounded border hover:bg-[hsl(var(--surface-2))] flex-none"
            style={{ borderColor: 'hsl(var(--border))' }}
            data-testid={`milestone-done-btn-${projectId}-${index}`}
            title="Mark milestone as done (or swipe right)"
          >
            <CheckCircle2 size={11} className="inline mr-0.5" />Done
          </button>
        )}
        {canAct && isJustDone && (
          <button
            onClick={onClickUndo}
            className="text-[10.5px] px-2 py-[3px] rounded border hover:bg-[hsl(var(--surface-2))] flex-none"
            style={{ borderColor: 'hsl(var(--border))' }}
            data-testid={`milestone-undo-btn-${projectId}-${index}`}
            title="Undo locally (or swipe left)"
          >
            <RotateCcw size={11} className="inline mr-0.5" />Undo
          </button>
        )}
      </div>
    </div>
  );
}

/** Compact popover trigger that opens the milestone slider. Drops into the
 *  "Milestones" cell of the master-projects table. */
export function MilestoneSliderPopover({ project, canAct, onChanged }) {
  const count = (project.milestones || []).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="text-[12px] underline decoration-dotted underline-offset-2 hover:text-[hsl(var(--primary))]"
          data-testid={`milestone-slider-open-${project.id}`}
          title={count ? 'Open milestone slider — swipe right to complete' : 'No milestones yet'}
        >
          {count}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-3 w-[360px]" side="right" align="start" data-testid={`milestone-slider-popover-${project.id}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[12.5px] font-semibold">{project.project_name}</div>
          {canAct && (
            <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))]">
              swipe → done · swipe ← undo
            </div>
          )}
        </div>
        <MilestoneSlider project={project} canAct={canAct} onChanged={onChanged} />
      </PopoverContent>
    </Popover>
  );
}
